import LightCustomButton from "@components/buttons/LightCustomButton";
import LabelRow from "@components/rowcomponents/LabelRow";
import { kycService } from "@services/apis/Kyc";
import { FetchKycDataForApprovalData } from "@services/models/Kyc/FetchKycDataForApprovalModel";
import React, { useEffect, useState } from "react";
import useSnackbarMessage from "../../utils/HandleSuccessErrorSnackbars";
import ConstantFunctions from "../../utils/constants_functions";

interface KycApprovalDetailsProps {
  data: FetchKycDataForApprovalData;
  onRefresh: () => void;
}

// Interface for POI/POA images
interface IdImages {
  poiImages: {
    frontImage: string | null;
    selfieImage: string | null;
  };
  poaImages: {
    frontImage: string | null;
    backImage: string | null;
  };
}

// POI/POA status options
const poiPoaStatusOptions = [
  "pending",
  "approved",
  "needs_review",
  "declined",
  "retry",
];

// Overall KYC status options
const overallKycStatusOptions = [
  "incomplete",
  "pending_review",
  "approved",
  "rejected",
  "retry",
];

const KycApprovalDetails: React.FC<KycApprovalDetailsProps> = ({
  data,
  onRefresh,
}) => {
  const constantFunctions = new ConstantFunctions();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedStatuses, setSelectedStatuses] = useState<{
    poiStatus: string;
    poaStatus: string;
    overallKycStatus: string;
  }>({
    poiStatus: data.poiStatus ? String(data.poiStatus) : "",
    poaStatus: data.poaStatus ? String(data.poaStatus) : "",
    overallKycStatus: data.overallKycStatus
      ? String(data.overallKycStatus)
      : "",
  });
  const [idImages, setIdImages] = useState<IdImages>({
    poiImages: {
      frontImage: null,
      selfieImage: null,
    },
    poaImages: {
      frontImage: null,
      backImage: null,
    },
  });
  const { handleSuccess, handleError } = useSnackbarMessage();

  useEffect(() => {
    setSelectedStatuses({
      poiStatus: data.poiStatus ? String(data.poiStatus) : "",
      poaStatus: data.poaStatus ? String(data.poaStatus) : "",
      overallKycStatus: data.overallKycStatus
        ? String(data.overallKycStatus)
        : "",
    });
  }, [data.poiStatus, data.poaStatus, data.overallKycStatus]);

  // Extract ID images from poiDump and poaDump
  useEffect(() => {
    const extractedImages: IdImages = {
      poiImages: {
        frontImage: null,
        selfieImage: null,
      },
      poaImages: {
        frontImage: null,
        backImage: null,
      },
    };

    try {
      // Process POI dump (ID document)
      if (data.poiDump) {
        // Check if it's the complex format with result.results
        if (data.poiDump.result && data.poiDump.result.results) {
          const results = data.poiDump.result.results;
          const userDetails = data.poiDump.result.userDetails;

          // Look for ID card validation front
          const idCardModule = results.find(
            (item: any) =>
              item.module &&
              (item.module.includes("ID Card Validation") ||
                item.module.includes("Aadhaar OCR front") ||
                item.module.includes("PAN"))
          );

          if (idCardModule) {
            extractedImages.poiImages.frontImage =
              idCardModule.imageUrl ||
              idCardModule.apiResponse?.result?.inputImageUrls?.image;
          }

          // Look for selfie
          const selfieModule = results.find(
            (item: any) => item.module && item.module.includes("Selfie")
          );

          if (selfieModule) {
            extractedImages.poiImages.selfieImage =
              selfieModule.imageUrl ||
              selfieModule.apiResponse?.result?.inputImageUrls?.image;
          }

          // Look in userDetails for specific URLs
          if (userDetails) {
            if (!extractedImages.poiImages.frontImage) {
              extractedImages.poiImages.frontImage =
                userDetails[
                  "ID_Card_Validation_ID_Front_Image_-_Full_(S3_URL)"
                ] || userDetails["Aadhaar_OCR_ID_Front_Image_-_Full_(S3_URL)"];
            }

            if (!extractedImages.poiImages.selfieImage) {
              extractedImages.poiImages.selfieImage =
                userDetails["Selfie_Validation_Selfie_Image_URL_(S3_URL)"];
            }
          }
        }
        // Handle the simple format with direct documentUrl
        else if (data.poiDump.documentUrl) {
          extractedImages.poiImages.frontImage = data.poiDump.documentUrl;
        }
      }

      // Process POA dump (address document)
      if (data.poaDump) {
        // Check if it's the complex format with result.results
        if (data.poaDump.result && data.poaDump.result.results) {
          const results = data.poaDump.result.results;
          const userDetails = data.poaDump.result.userDetails;

          // Look for address document images
          const addressFrontModule = results.find(
            (item: any) => item.module && item.module.includes("front")
          );

          if (addressFrontModule) {
            extractedImages.poaImages.frontImage =
              addressFrontModule.imageUrl ||
              addressFrontModule.apiResponse?.result?.inputImageUrls?.image;
          }

          const addressBackModule = results.find(
            (item: any) => item.module && item.module.includes("back")
          );

          if (addressBackModule) {
            extractedImages.poaImages.backImage =
              addressBackModule.imageUrl ||
              addressBackModule.apiResponse?.result?.inputImageUrls?.image;
          }

          // Check userDetails for address document images
          if (userDetails) {
            if (!extractedImages.poaImages.frontImage) {
              extractedImages.poaImages.frontImage =
                userDetails["Aadhaar_OCR_ID_Front_Image_-_Full_(S3_URL)"] ||
                userDetails["Address_Proof_Front_Image_-_Full_(S3_URL)"];
            }

            if (!extractedImages.poaImages.backImage) {
              extractedImages.poaImages.backImage =
                userDetails["Aadhaar_OCR_ID_Back_Image_-_Full_(S3_URL)"] ||
                userDetails["Address_Proof_Back_Image_-_Full_(S3_URL)"];
            }
          }
        }
        // Handle the simple format with direct documentUrl
        else if (data.poaDump.documentUrl) {
          extractedImages.poaImages.frontImage = data.poaDump.documentUrl;

          // If there's a documentBackUrl, use it for back image
          if (data.poaDump.documentBackUrl) {
            extractedImages.poaImages.backImage = data.poaDump.documentBackUrl;
          }
        }
      }

      // Face match module might contain selfie image
      const checkFaceMatchInDump = (dump: any) => {
        if (dump && dump.result && dump.result.results) {
          const faceMatch = dump.result.results.find(
            (item: any) => item.module && item.module.includes("Face Match")
          );

          if (faceMatch && !extractedImages.poiImages.selfieImage) {
            extractedImages.poiImages.selfieImage =
              faceMatch.selfieImageUrl ||
              faceMatch.apiResponse?.result?.inputImageUrls?.selfie;
          }
        }
      };

      // Check both dumps for face match
      if (!extractedImages.poiImages.selfieImage) {
        checkFaceMatchInDump(data.poiDump);
        checkFaceMatchInDump(data.poaDump);
      }

      setIdImages(extractedImages);
    } catch (error) {
      console.error("Error extracting ID images", error);
    }
  }, [data.poiDump, data.poaDump]);

  const handleStatusChange = (
    statusType: "poiStatus" | "poaStatus" | "overallKycStatus",
    value: string
  ) => {
    setSelectedStatuses((prev) => ({
      ...prev,
      [statusType]: value,
    }));
  };

  const updateKycStatus = async () => {
    try {
      setIsLoading(true);

      await kycService.updateKYCStatus({
        userId: data.userId.toString(),
        poiStatus: selectedStatuses.poiStatus,
        poaStatus: selectedStatuses.poaStatus,
        overallKycStatus: selectedStatuses.overallKycStatus,
      });

      // Show success message
      handleSuccess({
        message: "KYC status updated successfully",
      });

      // Refresh the data
      onRefresh();
    } catch (err: any) {
      handleError({
        message: `Failed to update KYC status: ${
          err.message || "Unknown error"
        }`,
      });
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-[12px] overflow-auto" style={{height:"80vh"}}>
      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <div className="flex flex-row justify-between">
          <p className="text-text-base text-[14px] font-medium">
            User Information
          </p>
        </div>
        <LabelRow label="Name" value={`${data.firstName} ${data.lastName}`} />
        <LabelRow label="Email" value={data.email} />
        <LabelRow label="User ID" value={data.userId.toString()} />
        <LabelRow label="Country" value={data.userCountry || "- -"} />
        <div className="border-b border-border-subtle"></div>
        <LabelRow
          label="Date of Birth"
          value={constantFunctions.dateFormatInDDMMYY(data.dateOfBirth)}
        />
        <LabelRow
          label="Created At"
          value={constantFunctions.formatDateTime(data.createdAt)}
        />
        <LabelRow
          label="Updated At"
          value={constantFunctions.formatDateTime(data.updatedAt)}
        />
      </div>

      {/* POI Images Section */}
      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <div className="flex flex-row justify-between">
          <p className="text-text-base text-[14px] font-medium">
            ID Verification (POI) Images
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {idImages.poiImages.frontImage ? (
            <div className="flex flex-col items-center">
              <p className="text-text-subtle text-[10px] mb-1">POI Image</p>
              <img
                src={idImages.poiImages.frontImage}
                alt="POI"
                className="w-full object-contain border border-border-subtle rounded-md"
                style={{ maxHeight: "150px" }}
              />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-[150px] bg-gray-50 border border-border-subtle rounded-md">
              <p className="text-text-subtle text-[10px]">
                POI Not Available
              </p>
            </div>
          )}

          {idImages.poiImages.selfieImage ? (
            <div className="flex flex-col items-center">
              <p className="text-text-subtle text-[10px] mb-1">Selfie</p>
              <img
                src={idImages.poiImages.selfieImage}
                alt="Selfie"
                className="w-full object-contain border border-border-subtle rounded-md"
                style={{ maxHeight: "150px" }}
              />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-[150px] bg-gray-50 border border-border-subtle rounded-md">
              <p className="text-text-subtle text-[10px]">
                Selfie Not Available
              </p>
            </div>
          )}
        </div>
      </div>

      {/* POA Images Section */}
      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <div className="flex flex-row justify-between">
          <p className="text-text-base text-[14px] font-medium">
            Address Verification (POA) Images
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {idImages.poaImages.frontImage ? (
            <div className="flex flex-col items-center">
              <p className="text-text-subtle text-[10px] mb-1">
                Address Document Front
              </p>
              <img
                src={idImages.poaImages.frontImage}
                alt="Address Front"
                className="w-full object-contain border border-border-subtle rounded-md"
                style={{ maxHeight: "150px" }}
              />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-[150px] bg-gray-50 border border-border-subtle rounded-md">
              <p className="text-text-subtle text-[10px]">
                Address Document Front Not Available
              </p>
            </div>
          )}

          {idImages.poaImages.backImage ? (
            <div className="flex flex-col items-center">
              <p className="text-text-subtle text-[10px] mb-1">
                Address Document Back
              </p>
              <img
                src={idImages.poaImages.backImage}
                alt="Address Back"
                className="w-full object-contain border border-border-subtle rounded-md"
                style={{ maxHeight: "150px" }}
              />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-[150px] bg-gray-50 border border-border-subtle rounded-md">
              <p className="text-text-subtle text-[10px]">
                Address Document Back Not Available
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <div className="flex flex-row justify-between">
          <p className="text-text-base text-[14px] font-medium">KYC Status</p>
        </div>
        <LabelRow
          label="Overall KYC Status"
          value={data.overallKycStatus || "Pending"}
        />
        <div className="border-b border-border-subtle"></div>
        <LabelRow
          label="ID Verification Status"
          value={data.poiStatus || "Pending"}
        />
        <LabelRow label="ID Verification Type" value={data.poiType || "- -"} />
        <LabelRow label="ID Number" value={data.poiNumber || "- -"} />
        {data.poiCompletedAt && (
          <LabelRow
            label="ID Verified At"
            value={constantFunctions.formatDateTime(data.poiCompletedAt)}
          />
        )}
        <div className="border-b border-border-subtle"></div>
        <LabelRow
          label="Address Verification Type"
          value={data.poaType || "- -"}
        />
        <div className="flex flex-col gap-[4px]">
          <span className="text-text-base text-[12px] font-normal">
            Address
          </span>
          <span className="font-normal text-[12px] text-text-base break-words whitespace-normal">
            {`${data.addressLine1 || ""}, ${data.city || ""}, ${
              data.stateProvinceRegion || ""
            }, ${data.postalCode || ""}`.replace(/^,\s*|,\s*$/g, "") || "- -"}
          </span>
        </div>
        {data.poaCompletedAt && (
          <LabelRow
            label="Address Verified At"
            value={constantFunctions.formatDateTime(data.poaCompletedAt)}
          />
        )}
        {(data.faceMatchScore || data.livenessScore) && (
          <>
            <div className="border-b border-border-subtle"></div>
            {data.faceMatchScore && (
              <LabelRow
                label="Face Match Score"
                value={data.faceMatchScore.toString()}
              />
            )}
            {data.livenessScore && (
              <LabelRow
                label="Liveness Score"
                value={data.livenessScore.toString()}
              />
            )}
          </>
        )}
        <LabelRow
          label="POI Approved By"
          value={data.poiApprovedByEmail || "- -"}
        />
        <LabelRow
          label="POA Approved By"
          value={data.poaApprovedByEmail || "- -"}
        />
      </div>
      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <div className="flex flex-row justify-between">
          <p className="text-text-base text-[14px] font-medium">KYC Actions</p>
        </div>

        <div className="grid grid-cols-1 gap-4 pt-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ID Verification Status (POI)
            </label>
            <select
              className="mt-1 block w-full pl-3 pr-10 py-2 text-xs border border-border-subtle rounded-md focus:outline-none focus:ring-primary focus:border-primary"
              value={selectedStatuses.poiStatus}
              onChange={(e) => handleStatusChange("poiStatus", e.target.value)}
            >
              <option value="">-- Select POI Status --</option>
              {poiPoaStatusOptions.map((status) => (
                <option key={`poi-${status}`} value={status}>
                  {status.replace("_", " ").charAt(0).toUpperCase() +
                    status.replace("_", " ").slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address Verification Status (POA)
            </label>
            <select
              className="mt-1 block w-full pl-3 pr-10 py-2 text-xs border border-border-subtle rounded-md focus:outline-none focus:ring-primary focus:border-primary"
              value={selectedStatuses.poaStatus}
              onChange={(e) => handleStatusChange("poaStatus", e.target.value)}
            >
              <option value="">-- Select POA Status --</option>
              {poiPoaStatusOptions.map((status) => (
                <option key={`poa-${status}`} value={status}>
                  {status.replace("_", " ").charAt(0).toUpperCase() +
                    status.replace("_", " ").slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Overall KYC Status
            </label>
            <select
              className="mt-1 block w-full pl-3 pr-10 py-2 text-xs border border-border-subtle rounded-md focus:outline-none focus:ring-primary focus:border-primary"
              value={selectedStatuses.overallKycStatus}
              onChange={(e) =>
                handleStatusChange("overallKycStatus", e.target.value)
              }
            >
              <option value="">-- Select Overall Status --</option>
              {overallKycStatusOptions.map((status) => (
                <option key={`overall-${status}`} value={status}>
                  {status.replace("_", " ").charAt(0).toUpperCase() +
                    status.replace("_", " ").slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-center pt-2">
            <LightCustomButton
              text="Update KYC Status"
              isActive={true}
              onClick={updateKycStatus}
              isButtonLoading={isLoading}
              textSize="12px"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default KycApprovalDetails;
